import { type AbortableResponse, fetchWithTimeout } from './fetch-with-timeout';

interface CacheValue {
  response: Promise<AbortableResponse>;
  expiresAt: number;
}

const DEFAULT_CACHE_TTL_MS = 1000 * 60 * 60 * 24; // 1 day
const CACHED_FETCH_RESULTS: Record<string, CacheValue> = {};

export interface FetchWithMemoryCacheOptions {
  cacheKey: string;
  fetcher?: typeof fetchWithTimeout | typeof fetch;
  ttlMs?: number;
}

/**
 * Stores the response in memory and contains fetch mutex. Next time it is fetches it will return
 * the cached value. Does not cache failed requests.
 *
 * Warning: clones the response object on subsequent calls
 */
export const fetchWithMemoryCache = async (
  url: string,
  requestOptions: RequestInit = {},
  {
    cacheKey,
    fetcher = fetchWithTimeout,
    ttlMs = DEFAULT_CACHE_TTL_MS,
  }: FetchWithMemoryCacheOptions
): Promise<AbortableResponse> => {
  const { response: cachedResponse, expiresAt } = CACHED_FETCH_RESULTS[cacheKey] || {};
  const now = Date.now();

  if (cachedResponse && expiresAt > now) {
    return cachedResponse.then(copy);
  }

  const response = fetcher(url, { ...requestOptions });
  const newExpiresAt = now + ttlMs;

  CACHED_FETCH_RESULTS[cacheKey] = {
    response,
    expiresAt: newExpiresAt,
  };

  const res = await response;

  // only cache successful responses
  if (!res.ok) {
    delete CACHED_FETCH_RESULTS[cacheKey];
  }

  if (res.status === 408) {
    return res;
  }

  return copy(res);
};

const copy = (response: AbortableResponse) => {
  if (response.status === 408) {
    return response;
  }

  return (response as Response).clone();
};

export const clearCachedFetches = () => {
  for (const key of Object.keys(CACHED_FETCH_RESULTS)) {
    delete CACHED_FETCH_RESULTS[key];
  }
};
